'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface RoadmapItem {
  quarter: string;
  year: string;
  title: string;
  description: string;
  features: string[];
  status: 'completed' | 'in-progress' | 'planned';
}

interface FutureRoadmapProps {
  roadmap: RoadmapItem[];
}

export default function FutureRoadmap({ roadmap }: FutureRoadmapProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-[#42AF47]';
      case 'in-progress':
        return 'bg-amber-500';
      case 'planned':
        return 'bg-gray-400';
      default:
        return 'bg-gray-300';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in-progress':
        return 'In Progress';
      case 'planned':
        return 'Planned';
      default:
        return 'Unknown';
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-gray-900 mb-4">
            Future Roadmap
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Upcoming features and city expansion plans to revolutionize urban parking
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Vertical timeline line on desktop */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block"></div>

            <div className="space-y-12 md:space-y-16">
              {roadmap.map((item, index) => (
                <div key={index} className="relative">
                  <div className="md:flex md:items-start md:space-x-10">
                    {/* Timeline node */}
                    <div className="flex-shrink-0 mb-6 md:mb-0 flex justify-center md:justify-start">
                      <div className="relative">
                        <div
                          className={`w-16 h-16 ${getStatusColor(
                            item.status
                          )} rounded-full flex items-center justify-center shadow-md border-4 border-white`}
                        >
                          <Icon
                            name={
                              item.status === 'completed'
                                ? 'CheckIcon'
                                : item.status === 'in-progress'
                                ? 'ClockIcon'
                                : 'CalendarIcon'
                            }
                            size={28}
                            className="text-white"
                          />
                        </div>
                        {/* Status badge below node */}
                        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap">
                          <span
                            className={`text-xs font-semibold px-4 py-1.5 rounded-full ${getStatusColor(
                              item.status
                            )} text-white shadow-sm`}
                          >
                            {getStatusText(item.status)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Content card */}
                    <div className="flex-1 bg-white rounded-2xl p-7 md:p-8 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-[#42AF47]/30">
                      <div className="flex items-center justify-between mb-5">
                        <div>
                          <p className="text-sm text-gray-500 font-medium mb-1">
                            {item.quarter} {item.year}
                          </p>
                          <h3 className="text-2xl font-heading font-bold text-gray-900">
                            {item.title}
                          </h3>
                        </div>
                      </div>

                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {item.description}
                      </p>

                      <div className="space-y-3">
                        {item.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-6 h-6 bg-[#42AF47]/10 rounded-full flex items-center justify-center mt-0.5">
                              <Icon name="ArrowRightIcon" size={14} className="text-[#42AF47]" />
                            </div>
                            <span className="text-gray-700 text-base">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}